/**
 * Group Details Page
 * Displays detailed information about a group and its members
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Container,
  Paper,
  Avatar,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
} from '@mui/material'
import {
  Groups as GroupsIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  ExitToApp as LeaveIcon,
  Edit as EditIcon,
  PersonAdd as InviteIcon,
  ArrowBack as BackIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon,
  Shield as ModeratorIcon,
  Person as PersonIcon,
  Star as OwnerIcon,
  LocalOffer as TagIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon,
  RemoveCircle as RemoveIcon,
  Block as BlockIcon,
  Chat as ChatIcon,
  CardGiftcard as RewardsIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { profileApi } from '@api/endpoints/profile'
import { chatApi } from '@api/endpoints/chat'
import { GroupRole, getGroupRoleName, GroupVisibility, getGroupVisibilityName, GroupPermissionLevel } from '@api/types/social'
import { useAuthStore } from '@store/authStore'
import { useCachedTags } from '@/hooks/useCachedTags'
import { TagType } from '@/features/adventures/types/adventure-editor.types'
import { EditGroupDialog } from '../components/EditGroupDialog'
import { InviteGroupDialog } from '../components/InviteGroupDialog'
import { GroupDiscussions } from '../components/GroupDiscussions'
import { JoinRequestsManagement } from '../components/JoinRequestsManagement'
import { BannedUsersManagement } from '../components/BannedUsersManagement'
import { GroupRewardsDialog } from '@features/rewards/components/GroupRewardsDialog'
import { canUserPerformAction, getPermissionDeniedMessage } from '@utils/groupPermissions'

export function GroupDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentTab, setCurrentTab] = useState(0)
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<{ element: HTMLElement; userId: string; currentRole: GroupRole } | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [rewardsDialogOpen, setRewardsDialogOpen] = useState(false)
  const { user } = useAuthStore() // Get user at top level to avoid hooks rule violation
  const { getCategoriesByType } = useCachedTags()

  // Initialize tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam) {
      const tabMap: Record<string, number> = {
        overview: 0,
        members: 1,
        discussions: 2,
        requests: 3,
        bans: 4,
      }
      const tabIndex = tabMap[tabParam]
      if (tabIndex !== undefined) {
        setCurrentTab(tabIndex)
      }
    }
  }, [searchParams])

  // Keyboard shortcuts for tab navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle shortcuts when not typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      // ALT + Number keys 1-5 for tabs
      if (e.altKey && e.key >= '1' && e.key <= '5') {
        e.preventDefault() // Prevent default browser behavior
        const tabIndex = parseInt(e.key) - 1
        setCurrentTab(tabIndex)
        updateTabUrl(tabIndex)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  const updateTabUrl = (tabIndex: number) => {
    const tabNames = ['overview', 'members', 'discussions', 'requests', 'bans']
    const discussionId = searchParams.get('discussionId')
    const params: Record<string, string> = { tab: tabNames[tabIndex] }
    if (discussionId && tabIndex === 2) {
      params.discussionId = discussionId
    }
    setSearchParams(params, { replace: true })
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    // Check if the tab is allowed based on permissions
    let isAllowed = true
    let deniedMessage = ''

    if (newValue === 1 && !canViewMembers) {
      isAllowed = false
      deniedMessage = getPermissionDeniedMessage(permissions?.viewMembersLevel || GroupPermissionLevel.Members, 'View Members')
    } else if (newValue === 2 && !canViewDiscussions) {
      isAllowed = false
      deniedMessage = getPermissionDeniedMessage(permissions?.viewDiscussionsLevel || GroupPermissionLevel.Members, 'View Discussions')
    }

    if (!isAllowed) {
      alert(deniedMessage)
      return
    }

    setCurrentTab(newValue)
    updateTabUrl(newValue)
  }

  // Fetch group details
  const { data: group, isLoading, error } = useQuery({
    queryKey: ['group', id],
    queryFn: () => socialApi.getGroup(id!),
    enabled: !!id,
  })

  // Fetch discussions to get count
  const { data: discussions } = useQuery({
    queryKey: ['discussions', id],
    queryFn: () => socialApi.getDiscussions(id!),
    enabled: !!id && (group?.isUserMember || (group?.visibility ?? (group?.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.Public) === true,
    refetchOnWindowFocus: false,
  })

  // Fetch user's profile to check for matching tags
  const { data: userProfile } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => profileApi.getMyProfile(),
    enabled: !!user, // Only fetch if user is logged in
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Join group mutation
  const joinGroupMutation = useMutation({
    mutationFn: () => socialApi.joinGroup(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', id] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })

  // Leave group mutation
  const leaveGroupMutation = useMutation({
    mutationFn: () => socialApi.leaveGroup(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      navigate('/trail-buddies?tab=groups')
    },
  })

  // Delete group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: () => socialApi.deleteGroup(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['all-groups'] })
      navigate('/trail-buddies?tab=groups')
    },
  })

  // Update member role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: GroupRole }) =>
      socialApi.updateMemberRole(id!, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', id] })
      setMemberMenuAnchor(null)
    },
  })

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => socialApi.removeGroupMember(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', id] })
      queryClient.invalidateQueries({ queryKey: ['groupMembers', id] })
      setMemberMenuAnchor(null)
    },
  })

  // Ban member mutation
  const banMemberMutation = useMutation({
    mutationFn: (userId: string) => socialApi.banUser(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', id] })
      queryClient.invalidateQueries({ queryKey: ['groupMembers', id] })
      queryClient.invalidateQueries({ queryKey: ['groupBans', id] })
      setMemberMenuAnchor(null)
    },
  })

  const handleRemoveMember = () => {
    if (memberMenuAnchor) {
      removeMemberMutation.mutate(memberMenuAnchor.userId)
    }
  }

  const handleBanMember = () => {
    if (memberMenuAnchor) {
      banMemberMutation.mutate(memberMenuAnchor.userId)
    }
  }

  const handleJoinGroup = () => {
    joinGroupMutation.mutate()
  }

  const handleLeaveGroup = () => {
    if (group && confirm(`Are you sure you want to leave ${group.name}?`)) {
      leaveGroupMutation.mutate()
    }
  }

  const handleDeleteGroup = () => {
    if (group && confirm(`Are you sure you want to permanently delete ${group.name}? This action cannot be undone.`)) {
      deleteGroupMutation.mutate()
    }
  }

  const handleBack = () => {
    navigate('/trail-buddies?tab=groups')
  }

  const handleOpenMemberMenu = (event: React.MouseEvent<HTMLElement>, userId: string, currentRole: GroupRole) => {
    setMemberMenuAnchor({ element: event.currentTarget, userId, currentRole })
  }

  const handleCloseMemberMenu = () => {
    setMemberMenuAnchor(null)
  }

  const handleChangeRole = (newRole: GroupRole) => {
    if (memberMenuAnchor) {
      updateRoleMutation.mutate({ userId: memberMenuAnchor.userId, role: newRole })
    }
  }

  const getRoleIcon = (role: GroupRole) => {
    switch (role) {
      case GroupRole.Owner:
        return <OwnerIcon />
      case GroupRole.Admin:
        return <AdminIcon />
      case GroupRole.Moderator:
        return <ModeratorIcon />
      default:
        return <PersonIcon />
    }
  }

  const getRoleColor = (role: GroupRole): 'primary' | 'secondary' | 'success' | 'default' => {
    switch (role) {
      case GroupRole.Owner:
        return 'primary'
      case GroupRole.Admin:
        return 'secondary'
      case GroupRole.Moderator:
        return 'success'
      default:
        return 'default'
    }
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error || !group) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Failed to load group details'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack}>
          Back to Groups
        </Button>
      </Container>
    )
  }

  // Handle both possible field names from API
  // The API returns userRole as a number (enum value)
  // Only set userRole if user is actually a member
  const userRole = group.isUserMember ? (group.userRole ?? GroupRole.Member) : undefined
  const isOwner = userRole === GroupRole.Owner || userRole === 3 || group.isOwner === true
  const isAdmin = userRole === GroupRole.Admin || userRole === 2
  const canManage = isOwner || isAdmin

  // Check if user is a moderator (for discussion moderation)
  // A user is a moderator if they have:
  // 1. Group-level role of Moderator or higher (Moderator, Admin, Owner), OR
  // 2. System-level role of Moderator or Admin
  const hasSystemModeratorRole = user?.roles?.some(role =>
    role === 'Moderator' || role === 'Admin'
  ) ?? false
  const hasGroupModeratorRole = userRole >= GroupRole.Moderator
  const isModerator = hasSystemModeratorRole || hasGroupModeratorRole

  // Determine if user has matching tags with the group (for MatchingTags permission level)
  // Compare user's tags with group's tags
  const hasMatchingTags = (() => {
    if (!userProfile || !group) return false

    // Get user's tag IDs (handle both new and old field names)
    const userInterestTagIds = userProfile.interestTagIds || userProfile.interestTags || []
    const userLookingForTagIds = userProfile.lookingForTagIds || userProfile.lookingFor || []

    // Get group's tag IDs
    const groupInterestTagIds = group.interestTags?.map(tag => tag.id) || []
    const groupLookingForTagIds = group.lookingForTags?.map(tag => tag.id) || []

    // Check for any overlap in interest tags
    const hasMatchingInterests = userInterestTagIds.some(tagId =>
      groupInterestTagIds.includes(tagId)
    )

    // Check for any overlap in looking for tags
    const hasMatchingLookingFor = userLookingForTagIds.some(tagId =>
      groupLookingForTagIds.includes(tagId)
    )

    return hasMatchingInterests || hasMatchingLookingFor
  })()

  // Permission checks using the new permission system
  const permissions = group.permissionSettings
  const canViewMembers = !permissions || canUserPerformAction(
    group.isUserMember ? userRole : null,
    hasMatchingTags,
    permissions.viewMembersLevel
  )
  const canInviteUsers = !permissions || canUserPerformAction(
    group.isUserMember ? userRole : null,
    hasMatchingTags,
    permissions.inviteUsersLevel
  )
  const canViewTags = !permissions || canUserPerformAction(
    group.isUserMember ? userRole : null,
    hasMatchingTags,
    permissions.viewTagsLevel
  )
  const canViewDiscussions = !permissions || canUserPerformAction(
    group.isUserMember ? userRole : null,
    hasMatchingTags,
    permissions.viewDiscussionsLevel
  )
  const canCreateDiscussions = !permissions || canUserPerformAction(
    group.isUserMember ? userRole : null,
    hasMatchingTags,
    permissions.createDiscussionsLevel
  )
  const canParticipateInDiscussions = !permissions || canUserPerformAction(
    group.isUserMember ? userRole : null,
    hasMatchingTags,
    permissions.participateInDiscussionsLevel
  )

  // Get all tags with full data (icon, description, color)
  const interestCategories = getCategoriesByType(TagType.Interests)
  const lookingForCategories = getCategoriesByType(TagType.LookingFor)
  const adventureTypeCategories = getCategoriesByType(TagType.AdventureTypes)

  const allInterestTags = interestCategories.flatMap(cat => cat.tags || [])
  const allLookingForTags = lookingForCategories.flatMap(cat => cat.tags || [])
  const allAdventureTypeTags = adventureTypeCategories.flatMap(cat => cat.tags || [])

  // Enrich group tags with full tag data
  const enrichedInterestTags = group.interestTags?.map(groupTag => {
    const fullTag = allInterestTags.find(t => t.id === groupTag.id)
    return fullTag || groupTag
  }) || []

  const enrichedLookingForTags = group.lookingForTags?.map(groupTag => {
    const fullTag = allLookingForTags.find(t => t.id === groupTag.id)
    return fullTag || groupTag
  }) || []

  const enrichedAdventureTypeTags = group.adventureTypeTags?.map(groupTag => {
    const fullTag = allAdventureTypeTags.find(t => t.id === groupTag.id)
    return fullTag || groupTag
  }) || []

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 0, sm: 3 } }}>
      {/* Group Header */}
      <Paper sx={{ p: { xs: 0.5, sm: 2 }, mb: 2, borderRadius: { xs: 0, sm: 1 } }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Group Avatar */}
          <Avatar
            src={group.groupImage}
            variant="rounded"
            sx={{
              width: { xs: 56, sm: 80 },
              height: { xs: 56, sm: 80 },
              bgcolor: 'primary.main',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              borderRadius: 2,
            }}
          >
            <GroupsIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
          </Avatar>

          {/* Group Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Top Row: Breadcrumb, Created Date, Visibility, Role */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<BackIcon />}
                label="Groups"
                size="small"
                onClick={handleBack}
                sx={{ cursor: 'pointer' }}
              />
              <Typography variant="caption" color="text.secondary">/</Typography>
              <Chip
                label={`Created ${new Date((group.createdDate.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(group.createdDate)) ? group.createdDate : group.createdDate + 'Z').toLocaleDateString()}`}
                variant="outlined"
                size="small"
              />
              <Chip
                icon={
                  (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.Public ? <PublicIcon /> :
                  (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.MatchingTagRequired ? <TagIcon /> :
                  <LockIcon />
                }
                label={getGroupVisibilityName(group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly))}
                size="small"
                color={
                  (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.Public ? 'success' :
                  (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.MatchingTagRequired ? 'primary' :
                  'default'
                }
              />
              {group.isUserMember && userRole !== undefined && (
                <Chip
                  icon={getRoleIcon(userRole)}
                  label={getGroupRoleName(userRole)}
                  size="small"
                  color={getRoleColor(userRole)}
                />
              )}
            </Box>

            {/* Group Name Row */}
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 1 }}>
              {group.name}
            </Typography>

            {/* Description Row */}
            {group.description && (
              <Typography variant="body2" color="text.secondary">
                {group.description}
              </Typography>
            )}
          </Box>

          {/* Action Buttons - Right Side */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {canManage && (
              <Tooltip title="Edit Group">
                <IconButton
                  color="primary"
                  onClick={() => setEditDialogOpen(true)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {canManage && (
              <Tooltip title="Manage Rewards">
                <IconButton
                  color="primary"
                  onClick={() => setRewardsDialogOpen(true)}
                >
                  <RewardsIcon />
                </IconButton>
              </Tooltip>
            )}
            {group.canAccessChat && (
              <Tooltip title="Group Chat">
                <IconButton
                  color="primary"
                  onClick={async () => {
                    try {
                      const conversation = await chatApi.getOrJoinGroupChat(group.id)
                      navigate(`/chat?conversation=${conversation.id}`)
                    } catch (error) {
                      console.error('Failed to join group chat:', error)
                    }
                  }}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
            )}
            {canInviteUsers && (
              <Tooltip title="Invite Members">
                <IconButton
                  color="primary"
                  onClick={() => setInviteDialogOpen(true)}
                >
                  <InviteIcon />
                </IconButton>
              </Tooltip>
            )}
            {isOwner && group.memberCount <= 1 && (
              <Tooltip title="Delete Group">
                <IconButton
                  color="error"
                  onClick={handleDeleteGroup}
                  disabled={deleteGroupMutation.isPending}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {!group.isUserMember && (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) !== GroupVisibility.InviteOnly && (
              <Tooltip title="Join Group">
                <IconButton
                  color="primary"
                  onClick={handleJoinGroup}
                  disabled={joinGroupMutation.isPending}
                >
                  <GroupAddIcon />
                </IconButton>
              </Tooltip>
            )}
            {group.isUserMember && !isOwner && (
              <Tooltip title="Leave Group">
                <IconButton
                  color="error"
                  onClick={handleLeaveGroup}
                  disabled={leaveGroupMutation.isPending}
                >
                  <LeaveIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: { xs: 0, sm: 1 } }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Overview" />
          <Tab
            label={`Members (${group.members?.length || group.memberCount || 0})`}
            sx={!canViewMembers ? { opacity: 0.6 } : undefined}
          />
          <Tab
            label={`Discussions (${discussions?.length || 0})`}
            sx={!canViewDiscussions ? { opacity: 0.6 } : undefined}
          />
          {isModerator && (
            <Tab label="Requests" />
          )}
          {isModerator && (
            <Tab label="Bans" />
          )}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Paper sx={{ p: { xs: 0.5, sm: 2 }, borderRadius: { xs: 0, sm: 1 } }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            About This Group
          </Typography>
          {group.description ? (
            <Typography variant="body2" color="text.secondary">
              {group.description}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No description provided
            </Typography>
          )}

          {/* Tags Section */}
          {canViewTags && (enrichedInterestTags.length > 0 || enrichedLookingForTags.length > 0 || enrichedAdventureTypeTags.length > 0) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Interest Tags */}
                {enrichedInterestTags && enrichedInterestTags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      Interest Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {enrichedInterestTags.map((tag) => (
                        <Tooltip
                          key={tag.id}
                          title={tag.description || ''}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {tag.icon && <span>{tag.icon}</span>}
                                <span>{tag.displayName || tag.name}</span>
                              </Box>
                            }
                            size="medium"
                            sx={{
                              backgroundColor: tag.color ? `${tag.color}20` : undefined,
                              borderColor: tag.color || undefined,
                              '&:hover': {
                                backgroundColor: tag.color ? `${tag.color}40` : undefined,
                              },
                            }}
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Looking For Tags */}
                {enrichedLookingForTags && enrichedLookingForTags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      Looking For
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {enrichedLookingForTags.map((tag) => (
                        <Tooltip
                          key={tag.id}
                          title={tag.description || ''}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {tag.icon && <span>{tag.icon}</span>}
                                <span>{tag.displayName || tag.name}</span>
                              </Box>
                            }
                            size="medium"
                            sx={{
                              backgroundColor: tag.color ? `${tag.color}20` : undefined,
                              borderColor: tag.color || undefined,
                              '&:hover': {
                                backgroundColor: tag.color ? `${tag.color}40` : undefined,
                              },
                            }}
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Adventure Type Tags */}
                {enrichedAdventureTypeTags && enrichedAdventureTypeTags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      Adventure Types
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {enrichedAdventureTypeTags.map((tag) => (
                        <Tooltip
                          key={tag.id}
                          title={tag.description || ''}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {tag.icon && <span>{tag.icon}</span>}
                                <span>{tag.displayName || tag.name}</span>
                              </Box>
                            }
                            size="medium"
                            sx={{
                              backgroundColor: tag.color ? `${tag.color}20` : undefined,
                              borderColor: tag.color || undefined,
                              '&:hover': {
                                backgroundColor: tag.color ? `${tag.color}40` : undefined,
                              },
                            }}
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {currentTab === 1 && (
        <Paper sx={{ p: { xs: 0.5, sm: 2 }, borderRadius: { xs: 0, sm: 1 } }}>
          {!canViewMembers ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {getPermissionDeniedMessage(permissions?.viewMembersLevel || GroupPermissionLevel.Members, 'View members')}
              </Typography>
            </Box>
          ) : group.members && group.members.length > 0 ? (
            <List>
              {group.members.map((member, index) => (
                <Box key={member.userId}>
                  {index > 0 && <Divider variant="inset" component="li" />}
                  <ListItem
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    secondaryAction={
                      canManage && member.role !== GroupRole.Owner ? (
                        <IconButton
                          edge="end"
                          onClick={(e) => handleOpenMemberMenu(e, member.userId, member.role)}
                          disabled={updateRoleMutation.isPending}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      ) : null
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={member.userAvatar}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/profile/${member.userId}`)}
                      >
                        {member.displayName.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { color: 'primary.main' },
                            }}
                            onClick={() => navigate(`/profile/${member.userId}`)}
                          >
                            {member.displayName}
                          </Typography>
                          <Chip
                            icon={getRoleIcon(member.role)}
                            label={getGroupRoleName(member.role)}
                            size="small"
                            color={getRoleColor(member.role)}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          {member.userName && member.userName.toLowerCase() !== member.displayName.toLowerCase() && `@${member.userName} • `}
                          {member.hometown && `${member.hometown} • `}
                          Joined {new Date((member.joinedDate.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(member.joinedDate)) ? member.joinedDate : member.joinedDate + 'Z').toLocaleDateString()}
                        </>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No members yet
            </Typography>
          )}
        </Paper>
      )}

      {/* Discussions Tab */}
      {currentTab === 2 && (
        <>
          {!canViewDiscussions ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {getPermissionDeniedMessage(permissions?.viewDiscussionsLevel || GroupPermissionLevel.Members, 'View discussions')}
              </Typography>
            </Paper>
          ) : (
            <GroupDiscussions
              groupId={id!}
              canPost={canCreateDiscussions}
              canReply={canParticipateInDiscussions}
              isModerator={isModerator} // Pass moderator status for hide/unhide capabilities
              initialDiscussionId={searchParams.get('discussionId') || undefined}
              highlightedPostId={searchParams.get('postId') || undefined}
            />
          )}
        </>
      )}

      {/* Requests Tab - Only visible to moderators/admins */}
      {currentTab === 3 && isModerator && (
        <Paper sx={{ p: { xs: 0.5, sm: 2 }, borderRadius: { xs: 0, sm: 1 } }}>
          <JoinRequestsManagement groupId={id!} groupName={group.name} />
        </Paper>
      )}

      {/* Bans Tab - Only visible to moderators/admins */}
      {currentTab === 4 && isModerator && (
        <Paper sx={{ p: { xs: 0.5, sm: 2 }, borderRadius: { xs: 0, sm: 1 } }}>
          <BannedUsersManagement groupId={id!} groupName={group.name} />
        </Paper>
      )}

      {/* Member Role Menu */}
      <Menu
        anchorEl={memberMenuAnchor?.element}
        open={Boolean(memberMenuAnchor)}
        onClose={handleCloseMemberMenu}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <Typography variant="caption" color="text.secondary">
            Change Role
          </Typography>
        </MenuItem>
        <Divider />
        {memberMenuAnchor?.currentRole !== GroupRole.Member && (
          <MenuItem onClick={() => handleChangeRole(GroupRole.Member)}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Member</ListItemText>
          </MenuItem>
        )}
        {memberMenuAnchor?.currentRole !== GroupRole.Moderator && (
          <MenuItem onClick={() => handleChangeRole(GroupRole.Moderator)}>
            <ListItemIcon>
              <ModeratorIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Moderator</ListItemText>
          </MenuItem>
        )}
        {memberMenuAnchor?.currentRole !== GroupRole.Admin && (
          <MenuItem onClick={() => handleChangeRole(GroupRole.Admin)}>
            <ListItemIcon>
              <AdminIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Admin</ListItemText>
          </MenuItem>
        )}
        {isOwner && memberMenuAnchor?.currentRole !== GroupRole.Owner && (
          <MenuItem onClick={() => handleChangeRole(GroupRole.Owner)}>
            <ListItemIcon>
              <OwnerIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Owner</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={handleRemoveMember}
          disabled={removeMemberMutation.isPending || memberMenuAnchor?.currentRole === GroupRole.Owner}
        >
          <ListItemIcon>
            <RemoveIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Remove from Group</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleBanMember}
          disabled={banMemberMutation.isPending || memberMenuAnchor?.currentRole === GroupRole.Owner}
        >
          <ListItemIcon>
            <BlockIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Ban from Group</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Group Dialog */}
      {editDialogOpen && (
        <EditGroupDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          group={group}
        />
      )}

      {/* Invite Group Dialog */}
      {inviteDialogOpen && (
        <InviteGroupDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          group={group}
        />
      )}

      {/* Group Rewards Dialog */}
      {rewardsDialogOpen && (
        <GroupRewardsDialog
          open={rewardsDialogOpen}
          onClose={() => setRewardsDialogOpen(false)}
          groupId={id!}
        />
      )}
    </Container>
  )
}
